import { createAuthenticatedClient } from "../authenticatedClient";
import { AGENT_REGISTRY_API_VERSION, API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  AgentIdentitiesResponse,
  AgentListResponse,
  agentListResponseSchema,
  FilterOptionsResponse,
  filterOptionsResponseSchema,
  LabelsResponse,
  labelsResponseSchema,
} from "../schemas/agentManage.schema";

/**
 * ISPM identity list API (POST /ispm/v1/identity).
 * Used to load owner/identity options for policy assignment.
 */
const isMfeMode =
  typeof window !== "undefined" &&
  (window.location.hostname === "loopback" ||
    window.location.pathname.startsWith("/ECMv6") ||
    window.location.pathname.startsWith("/intelligence"));
const agentClient = createAuthenticatedClient(
  `${API_BASE_URL}/agent/${AGENT_REGISTRY_API_VERSION}/registry`,
);
const identityBasePath = isMfeMode
  ? `${API_BASE_URL}/ispm/v1`
  : `${API_BASE_URL}/api/ispm/v1`;
const identityClient = createAuthenticatedClient(identityBasePath);

export interface GetAgentsListParams {
  searchQuery: string;
  filters: Record<string, string[]>;
  sort: { field: string; direction: "asc" | "desc" };
  pagination: {
    page: number;
    limit: number;
  };
}

export interface GetFilterOptionsParams {
  target: string;
  targetKey?: string | null;
  searchQuery?: string;
  filters?: Record<string, string[]>;
  pagination: Record<string, number>;
}

export interface GetLabelsParam {
  filter: string;
  offset: number;
  max: number;
}

export interface SaveLabelsParams {
  agentId: string;
  labels: Record<string, string>;
}

export interface SaveOwnersParams {
  agentId: string;
  owners: {
    name: string;
    email: string;
    role: string;
    rank: number;
    source: string | null;
  }[];
}

export interface GetAgentIdentitiesParams {
  pageSize?: number;
  searchText?: string;
  filters?: unknown[];
}

type CriticalityLevel =
  | "Critical"
  | "High"
  | "Medium"
  | "Low"
  | "critical"
  | "high"
  | "medium"
  | "low"
  | null;

export interface SaveAgentParams {
  agentId: string;
  criticality: CriticalityLevel;
  criticalityOverride: boolean;
}

export const agentManageApi = {
  getAgentsList: async (payload: GetAgentsListParams) => {
    const data = await agentClient.post<AgentListResponse>(
      "/agents/search",
      payload,
    );
    const validated: AgentListResponse = safeValidate(
      agentListResponseSchema,
      data,
      "getAgentList",
    ) as AgentListResponse;

    return validated;
  },
  getFilterOptions: async (payload: GetFilterOptionsParams) => {
    const data = await agentClient.post<FilterOptionsResponse>(
      "/agents/filter-options",
      payload,
    );
    const validated: FilterOptionsResponse = safeValidate(
      filterOptionsResponseSchema,
      data,
      "getFilterOptions",
    ) as FilterOptionsResponse;

    return validated;
  },
  getLabels: async (params: GetLabelsParam) => {
    const queryParams = new URLSearchParams();

    queryParams.append("filter", String(params.filter));
    queryParams.append("offset", String(params.offset));
    queryParams.append("max", String(params.max));

    const url = `/agents/labels?${queryParams.toString()}`;

    const data = await agentClient.get<LabelsResponse>(url);
    const validated: LabelsResponse = safeValidate(
      labelsResponseSchema,
      data,
      "getLabels",
    ) as LabelsResponse;

    return validated;
  },
  saveLabels: async ({ agentId, labels }: SaveLabelsParams) => {
    const data = await agentClient.put<{ id: string }>(
      `/agents/${agentId}/labels`,
      { labels },
    );

    return data;
  },
  saveOwners: async ({ agentId, owners }: SaveOwnersParams) => {
    const data = await agentClient.put<{ id: string }>(
      `/agents/${agentId}/owners`,
      { owners },
    );

    return data;
  },
  getAgentIdentities: async (params: GetAgentIdentitiesParams = {}) => {
    const { pageSize = 1000, searchText = "", filters = [] } = params;

    const query = new URLSearchParams();

    if (pageSize) {
      query.set("pageSize", String(pageSize));
    }

    const url = `/identity?${query.toString()}`;
    const body = { searchText, filters };
    const response = await identityClient.post<AgentIdentitiesResponse>(
      url,
      body,
    );

    return response.data?.identities ?? [];
  },
  saveAgent: async ({
    agentId,
    criticality,
    criticalityOverride,
  }: SaveAgentParams) => {
    const data = await agentClient.put<{ id: string }>(`/agents/${agentId}`, {
      criticality,
      criticalityOverride,
    });

    return data;
  },
};
